import React, { useState } from 'react';
import { useLanguage } from './LanguageContext';
import { translations } from '../data/translations';
import { Phone, Mail, MessageCircle, Send, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - will be connected to backend later
    console.log('Form submitted:', formData);
    toast.success(
      language === 'en' 
        ? 'Thank you! We will contact you soon.' 
        : 'شكراً لك! سنتواصل معك قريباً.'
    );
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  const contactMethods = [
    {
      icon: Phone,
      label: t.contact.info.call,
      value: '+974 4411 5510',
      href: 'tel:+97444115510',
      color: 'from-teal-500 to-teal-600'
    },
    {
      icon: Phone,
      label: language === 'en' ? 'Call Us' : 'اتصل بنا',
      value: '+974 4430 3317',
      href: 'tel:+97444303317',
      color: 'from-teal-600 to-cyan-600'
    },
    {
      icon: MessageCircle,
      label: t.contact.info.whatsapp,
      value: '+974 5999 4112',
      href: 'https://wa.me/97459994112',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Mail,
      label: t.contact.info.email,
      value: 'info@rafangroup.com',
      href: 'mailto:info@rafangroup.com',
      color: 'from-blue-500 to-blue-600'
    }
  ];

  const services = [
    t.services.carRental.title,
    t.services.chauffeur.title,
    t.services.tours.title,
    t.services.usedCars.title,
    t.services.construction.title,
    t.services.realEstate.title
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-600/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-gray-400">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className={`block text-white mb-2 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t.contact.form.name}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                  placeholder={t.contact.form.name}
                />
              </div>

              {/* Email */}
              <div>
                <label className={`block text-white mb-2 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t.contact.form.email}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                  placeholder={t.contact.form.email}
                />
              </div>

              {/* Phone */}
              <div>
                <label className={`block text-white mb-2 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t.contact.form.phone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                  placeholder={t.contact.form.phone}
                />
              </div>

              {/* Service Selection */}
              <div>
                <label className={`block text-white mb-2 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t.contact.form.service}
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="">{t.contact.form.selectService}</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label className={`block text-white mb-2 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t.contact.form.message}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder={t.contact.form.message}
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/50 hover:scale-105"
              >
                <span className="relative z-10">{t.contact.form.submit}</span>
                <Send className="w-5 h-5 relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Contact Methods */}
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <a
                  key={index}
                  href={method.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-teal-500/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl hover:shadow-teal-500/20"
                >
                  <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <div className="text-gray-400 text-sm mb-1">{method.label}</div>
                      <div className="text-white font-semibold">{method.value}</div>
                    </div>
                  </div>
                </a>
              );
            })}

            {/* Location Info */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <div className="text-white font-semibold mb-2">
                    {language === 'en' ? 'Our Location' : 'موقعنا'}
                  </div>
                  <div className="text-gray-400 leading-relaxed">
                    {language === 'en' 
                      ? 'Building 49, D-Ring Road, Doha, Qatar'
                      : 'مبنى 49، طريق الدائري D، الدوحة، قطر'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};